
import React from 'react';
import { EventImageCarousel } from "./EventImageCarousel";
import { EventStatusBadge } from "./EventStatusBadge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { navigationService } from "@/services/navigationService";
import { AttendeeAvatars } from "./AttendeeAvatars";
import { mergeEventAndGuestImages } from "@/utils/eventImageUtils";
import { Event } from "@/types/event";
import { UrgencyBadge } from "../event-card/UrgencyBadge";
import { useEventDeadline } from "@/hooks/useEventDeadline";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EventOptionsMenu } from "./EventOptionsMenu";

interface EventBasicInfoProps {
  name: string;
  description?: string;
  eventDate: string | null;
  imageUrl?: string | null;
  eventImages?: {
    image_id: string;
    image_url: string;
    is_primary: boolean;
    display_order: number;
  }[];
  status: string;
  locationType?: 'specific' | 'flexible';
  flexibleLocationDescription?: string;
  preferredDates?: string[];
  preferredVenues?: string[];
  preferredTimeOfDay?: string[];
  selectedVenue?: {
    name: string;
    address: string;
  } | null;
  selectedDate?: string | null;
  host?: {
    full_name: string;
    avatar_url?: string | null;
  };
  isHost: boolean;
  eventId: string;
  event_deadline?: string | null;
  rsvpStatus?: 'invited' | 'accepted' | 'declined' | 'maybe';
  guestRsvpComplete?: boolean;
  hasRecommendations?: boolean;
  onEdit?: () => void;
  onRsvp?: () => void;
  attendees?: {
    full_name: string;
    avatar_url?: string | null;
    guest_avatar_url?: string | null;
    rsvp_status?: string;
    isHost?: boolean;
    user_id?: string;
    invite_id?: string;
  }[];
  event?: Event;
}

export const EventBasicInfo = ({
  name,
  description,
  eventDate,
  imageUrl,
  eventImages,
  status,
  host,
  isHost,
  eventId,
  event_deadline,
  hasRecommendations,
  onEdit,
  onRsvp,
  rsvpStatus,
  guestRsvpComplete,
  locationType,
  flexibleLocationDescription,
  preferredDates,
  preferredVenues,
  selectedVenue,
  selectedDate,
  preferredTimeOfDay,
  attendees = [],
  event
}: EventBasicInfoProps) => {
  const getInitials = (name: string) => {
    return name.split(' ').map(part => part[0]).join('').toUpperCase();
  };
  
  const handleBack = () => {
    navigationService.goBack();
  };
  
  const acceptedAttendees = attendees.filter(
    attendee => attendee.rsvp_status === 'accepted' && !attendee.isHost
  );
  
  const mergedImages = event ? mergeEventAndGuestImages(event) : eventImages;
  
  const { showBadge } = useEventDeadline(
    event_deadline || null,
    !!guestRsvpComplete,
    !!hasRecommendations
  );
  
  const isConfirmed = status === 'confirmed' || status === 'booked';
  
  return <div className="relative pt-4">
      {isConfirmed && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-green-50 p-3 flex items-center justify-center text-center shadow-sm">
          <p className="text-green-800 font-medium">
            {status === 'booked' ? "You're booked in!" : "We're processing your booking"}
          </p>
        </div>
      )}

      <div className={`px-4 mb-4 ${isConfirmed ? 'mt-12' : ''}`}>
        <div className="flex items-center justify-between mb-3">
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8 p-0" 
            onClick={handleBack}
          >
            <X className="h-5 w-5" />
          </Button>
          
          <EventOptionsMenu 
            eventId={eventId}
            isHost={isHost}
            status={status}
            onEdit={onEdit}
            onRsvp={onRsvp}
          />
        </div>
        
        <div className="flex justify-between items-start mb-2">
          <h1 className="font-bold text-lg">{name}</h1>
        </div>
        
        {acceptedAttendees.length > 0 && (
          <div className="mb-4">
            <AttendeeAvatars attendees={acceptedAttendees} />
          </div>
        )}
      </div>

      <div className="relative">
        <EventImageCarousel 
          images={mergedImages} 
          fallbackImage={imageUrl} 
          eventTitle={name} 
          aspectRatio={1}
        />
        <div className="absolute top-8 right-8 flex gap-2">
          <EventStatusBadge status={status} />
        </div>
      </div>
      
      <div className="px-4 pt-4">
        {host && <div className="flex items-center gap-2 mb-4">
            <Avatar className="h-8 w-8">
              <AvatarImage src={host.avatar_url || undefined} alt={host.full_name} />
              <AvatarFallback>{getInitials(host.full_name)}</AvatarFallback>
            </Avatar>
            <span className="text-sm text-gray-700">
              {isHost ? 'You are hosting this event' : `Hosted by ${host.full_name}`}
            </span>
          </div>}

        {description && <div className="mb-6">
            <p className="text-gray-700 text-sm">{description}</p>
          </div>}
        
        {showBadge && event_deadline && !isConfirmed && (
          <div className="mb-4">
            <div className="mt-1">
              <UrgencyBadge 
                deadline={event_deadline} 
                label="to RSVP" 
                guestRsvpComplete={guestRsvpComplete} 
                hasRecommendations={hasRecommendations}
              />
            </div>
          </div>
        )}
      </div>
    </div>;
};
